/**
 * Slug/query-param resolution for onboarding short links (/hubspot,
 * /tool/xero, /xero?job_role=founder-ceo, ...). Pure functions only — the
 * resolver page (pages/Chat/OnboardingShortLink.jsx) supplies the current
 * agency/location's actual Tool/Category/Role/Sector options (there is no
 * global canonical list; each axis besides Tool is agency-configured data
 * fetched from `/onboarding-axis-options`).
 */

export const AXES = ["tool", "category", "role", "sector"];

// Case-insensitive, hyphen-and-punctuation-insensitive compare key. A slug
// only matches an option when they're equal after this normalization — no
// fuzzy/partial matching, so an unrecognized word never silently matches an
// unrelated option.
export function normalizeSlug(word) {
  return String(word || "")
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const listForAxis = (axis, options) => {
  if (axis === "tool") return options.tools || [];
  if (axis === "category") return options.categories || [];
  if (axis === "role") return options.roles || [];
  if (axis === "sector") return options.sectors || [];
  return [];
};

export const getAxisOptionLabel = (axis, item) => (axis === "tool" ? item.label ?? item.value : item.name);
export const getAxisOptionValue = (axis, item) => (axis === "tool" ? item.value : item.id);

/** Find one axis's option list entry by display label/name (used to resolve a URL's label back to its value). */
export function findAxisOption(axis, list, label) {
  const normalized = normalizeSlug(label);
  if (!normalized) return null;
  return (list || []).find((item) => normalizeSlug(getAxisOptionLabel(axis, item)) === normalized) || null;
}

/** Resolve a word against one specific axis (forced prefixes: /tool/xero, /role/..., /sector/..., /category/...). */
export function resolveForcedAxis(axis, word, options = {}) {
  if (!AXES.includes(axis)) return null;
  const found = findAxisOption(axis, listForAxis(axis, options), word);
  if (!found) return null;
  return { axis, value: getAxisOptionValue(axis, found), label: getAxisOptionLabel(axis, found) };
}

/** Resolve a word against every axis in precedence order: Tool, Category, Role, Sector. */
export function resolveAxisWord(word, options = {}) {
  const normalized = normalizeSlug(word);
  if (!normalized) return null;
  for (const axis of AXES) {
    const match = resolveForcedAxis(axis, word, options);
    if (match) return match;
  }
  return null;
}

// The role axis's URL key is "job_role" (matching the Agency model's
// job_roles field), not "role" — everything else about it (the AXES entry,
// axisConfig, selectedRoles/onToggleRole, the /role/:slug short link) still
// just says "role".
export const AXIS_QUERY_KEYS = { tool: "tool", category: "category", role: "job_role", sector: "sector" };

/**
 * Every value selected for one axis, from either comma-separated or repeated
 * query params (both mean OR within that axis), de-duped case-insensitively.
 */
export function getAxisValues(searchParams, axis) {
  const key = AXIS_QUERY_KEYS[axis];
  if (!key) return [];
  const seen = new Set();
  const values = [];
  searchParams.getAll(key).forEach((raw) => {
    raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .forEach((v) => {
        const k = v.toLowerCase();
        if (seen.has(k)) return;
        seen.add(k);
        values.push(v);
      });
  });
  return values;
}

/**
 * Sets `axis`'s OR-group on `params` from already-resolved display labels,
 * via the real query key (AXIS_QUERY_KEYS), not the axis name itself — the
 * two differ for role ("role" vs "job_role"). A call site that sets the
 * param directly using the axis name as the key is the bug this exists to
 * rule out (it bit `buildFilterSearchString` once already).
 */
export function setAxisLabels(params, axis, labels) {
  const key = AXIS_QUERY_KEYS[axis];
  if (!key || !labels.length) return;
  params.set(key, labels.join(","));
}

/**
 * Adds a resolved {axis, label} match into `search`'s OR-group for that axis
 * (deduped), leaving every other param — other axes, prompt/task text, task
 * ids, campaign tags — untouched.
 */
export function mergeAxisMatch(search, axis, label) {
  const params = new URLSearchParams(search);
  const key = AXIS_QUERY_KEYS[axis];
  if (!key) return params.toString();
  const values = getAxisValues(params, axis);
  if (!values.some((v) => v.toLowerCase() === label.toLowerCase())) {
    values.push(label);
  }
  params.delete(key);
  params.set(key, values.join(","));
  return params.toString();
}
